import { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { type LayoutChangeEvent, StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { SHOULD_USE_LEGACY_ELEVATION, theme } from '@garnish/constants';
import debounce from 'lodash.debounce';

import { useUniqueNativeID } from '../../../../../../hooks';
import { GlassView } from '../../../../../GlassView';
import {
  DIALOG_ANIMATIONS,
  DIALOG_PADDING,
  TOGGLE_SIZE,
  TOGGLE_SIZE_NEW,
} from '../../Floating.contants';
import { Toggle } from '../Toggle';
import { FadeGradient } from './components/FadeGradient';

// ─── Components ──────────────────────────────────────────────────────────────

export const Dialog = (props: Props) => {
  const {
    isShown,
    children,
    toggleA11yLabel,
    dialogA11yLabel,
    variant = 'standard',
    isLiquidGlassEnabled = false,
    onTogglePress,
  } = props;

  const { width: viewportWidth } = useWindowDimensions();
  const dialogNativeId = useUniqueNativeID('nutrition-calculator-dialog');

  // ─── Flags ───────────────────────────────────────────────────────────

  const isSmallScreen = viewportWidth <= 460;

  // ─── Derived Data ────────────────────────────────────────────────────

  const toggleSize = variant === 'compact' ? TOGGLE_SIZE_NEW : TOGGLE_SIZE;
  const dialogHeightExpanded = toggleSize + DIALOG_PADDING * 2;

  // ─── Shared Values ───────────────────────────────────────────────────

  const isShownSV = useSharedValue(0);
  const isContentShownSV = useSharedValue(0);
  const contentWidthSV = useSharedValue(0);
  const scrollXSV = useSharedValue(0);
  const scrollContentWidthSV = useSharedValue(0);

  const dialogWidthSV = useDerivedValue(
    () => contentWidthSV.value + toggleSize + DIALOG_PADDING,
    [toggleSize],
  );

  const leftGradientOpacity = useDerivedValue(() => {
    const hasOverflow = scrollContentWidthSV.value > contentWidthSV.value + 1;
    const shouldShow = scrollXSV.value > 0 && hasOverflow && isContentShownSV.value === 1;

    return withTiming(shouldShow ? 1 : 0, { duration: theme.transitions.short });
  });
  const rightGradientOpacity = useDerivedValue(() => {
    const hasOverflow = scrollContentWidthSV.value > contentWidthSV.value + 1;
    const isAtEnd = scrollXSV.value >= scrollContentWidthSV.value - contentWidthSV.value - 1;
    const shouldShow = hasOverflow && !isAtEnd && isContentShownSV.value === 1;

    return withTiming(shouldShow ? 1 : 0, { duration: theme.transitions.short });
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const storeContentWidth = useCallback(
    (width: number) => {
      'worklet';

      contentWidthSV.value = withTiming(width);
    },
    [contentWidthSV],
  );

  const storeContentWidthDebounced = debounce(storeContentWidth, 400);

  const storeScrollContentWidth = useCallback(
    (width: number) => {
      'worklet';

      scrollContentWidthSV.value = width;
    },
    [scrollContentWidthSV],
  );

  const onContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      storeContentWidthDebounced(event.nativeEvent.layout.width);
    },
    [storeContentWidthDebounced],
  );

  const onScrollContentSizeChange = useCallback(
    (contentWidth: number) => {
      storeScrollContentWidth(contentWidth);
    },
    [storeScrollContentWidth],
  );

  const toggleAnimations = useCallback(() => {
    if (!isShown) return;

    const {
      ENTRY_SPRING_MASS,
      EXIT_DURATION,
      EXIT_DELAY,
      CONTENT_ENTRY_DURATION,
      CONTENT_ENTRY_DELAY,
      CONTENT_EXIT_DURATION,
    } = DIALOG_ANIMATIONS;

    isShownSV.value = withSpring(1, { mass: ENTRY_SPRING_MASS }); // prettier-ignore
    isContentShownSV.value = withDelay(CONTENT_ENTRY_DELAY, withTiming(1, { duration: CONTENT_ENTRY_DURATION })); // prettier-ignore

    return () => {
      isShownSV.value = withDelay(EXIT_DELAY, withTiming(0, { duration: EXIT_DURATION }));
      isContentShownSV.value = withTiming(0, { duration: CONTENT_EXIT_DURATION });
    };
  }, [isContentShownSV, isShown, isShownSV]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const dialogAnimatedStyles = useAnimatedStyle(() => {
    const value = isShownSV.value;

    return {
      width: interpolate(value, [0, 1], [toggleSize, dialogWidthSV.value - DIALOG_PADDING + 16]),
      height: interpolate(value, [0, 1], [toggleSize, dialogHeightExpanded]),
      paddingRight: interpolate(value, [0, 1], [0, DIALOG_PADDING]),
    };
  });

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollXSV.value = event.contentOffset.x;
      scrollContentWidthSV.value = event.contentSize.width;
    },
  });

  const dialogVariantStyle = useMemo(() => {
    if (variant === 'compact') {
      return isShown ? styles.dialogCompact : undefined;
    }

    return styles.dialogStandard;
  }, [isShown, variant]);

  const contentContainerFluidStyle = useMemo(() => {
    if (variant === 'compact') {
      return styles.contentContainerFluidCompact;
    }

    return styles.contentContainerFluidStandard;
  }, [variant]);

  const dialogStyles = [
    dialogAnimatedStyles,
    styles.dialog,
    dialogVariantStyle,
    isLiquidGlassEnabled ? styles.dialogGlass : undefined,
  ];
  const contentContainerStyles = [
    isSmallScreen ? contentContainerFluidStyle : undefined,
    styles.contentContainer,
  ];

  const scrollViewAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: isContentShownSV.value };
  });
  const leftGradientAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: leftGradientOpacity.value };
  });
  const rightGradientAnimatedStyles = useAnimatedStyle(() => {
    return { opacity: rightGradientOpacity.value };
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(toggleAnimations, [toggleAnimations]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View
      pointerEvents="box-none"
      aria-expanded={isShown}
      style={isSmallScreen ? styles.containerSmallScreen : undefined}
    >
      <Animated.View style={dialogStyles} pointerEvents="box-none">
        {isLiquidGlassEnabled ? (
          <GlassView glassEffectStyle="regular" style={styles.glass} />
        ) : null}

        <Toggle
          isDialogShown={isShown}
          dialogNativeId={dialogNativeId}
          onPress={onTogglePress}
          accessibilityLabel={toggleA11yLabel}
          variant={variant}
          isLiquidGlassEnabled={isLiquidGlassEnabled}
        />
      </Animated.View>

      <View style={contentContainerStyles} pointerEvents={isShown ? 'auto' : 'none'}>
        <Animated.ScrollView
          accessibilityLabel={dialogA11yLabel}
          nativeID={dialogNativeId}
          style={[styles.scrollContainer, scrollViewAnimatedStyles]}
          onLayout={onContentLayout}
          onContentSizeChange={onScrollContentSizeChange}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.innerContentContainer}
          horizontal
          onScroll={scrollHandler}
          scrollEventThrottle={16}
        >
          {children}
        </Animated.ScrollView>

        {isSmallScreen ? (
          <>
            <FadeGradient style={[styles.gradientLeft, leftGradientAnimatedStyles]} />
            <FadeGradient reverse style={[styles.gradientRight, rightGradientAnimatedStyles]} />
          </>
        ) : null}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSmallScreen: {
    width: '100%',
    alignItems: 'flex-end',
  },
  dialog: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.WHITE,
    borderRadius: theme.radius['3xLarge'],
  },
  dialogStandard: {
    boxShadow: [
      {
        offsetX: 0,
        offsetY: theme.spacing['1'],
        blurRadius: theme.radius.xxlarge,
        color: 'rgba(0, 0, 0, 0.33)',
      },
    ],
    ...(SHOULD_USE_LEGACY_ELEVATION ? { elevation: 24 } : {}),
  },
  dialogCompact: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  dialogGlass: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    pointerEvents: 'none',
  },
  contentContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    flexDirection: 'row',
  },
  scrollContainer: {
    borderRadius: theme.radius['3xLarge'],
  },
  innerContentContainer: {
    alignItems: 'center',
  },
  contentContainerFluidStandard: {
    right: TOGGLE_SIZE + 16,
  },
  contentContainerFluidCompact: {
    right: TOGGLE_SIZE_NEW + 16,
  },
  gradientRight: {
    right: 0,
  },
  gradientLeft: {
    left: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  isShown: boolean;
  onTogglePress: () => void;
  dialogA11yLabel: string;
  toggleA11yLabel: string;
  variant?: 'compact' | 'standard';
  isLiquidGlassEnabled?: boolean;
}>;
